import { Injectable } from '@angular/core';
import { UserPreferencesStore } from '@app/core/stores/user-preferences-store.service';
import { TIME_RANGE_REQUEST_TYPE } from '@app/features/dashboard/enum/time-range';
import { DateRange } from '@app/features/dashboard/models/time-range-data.model';
import moment from 'moment';
import { DATE_FORMAT, TIME_INTERVAL } from '../enum/dates';

@Injectable()
export class DateService {
    private dateFormats: Map<DATE_FORMAT, string>;
    private dateWithTime: DATE_FORMAT[] = [];

    constructor(private userPreferencesStore: UserPreferencesStore) {
        this.dateFormats = new Map()
            .set(DATE_FORMAT.LONG_FULL_DATE, 'MMM D, Y')
            .set(DATE_FORMAT.LONG_FULL_DATE_TIME, 'MMM D, Y')
            .set(DATE_FORMAT.SHORT_FULL_DATE, 'M/D/Y')
            .set(DATE_FORMAT.SHORT_FULL_DATE_TIME, 'M/D/Y')
            .set(DATE_FORMAT.LONG_DATE, 'MMM D')
            .set(DATE_FORMAT.LONG_DATE_TIME, 'MMM D')
            .set(DATE_FORMAT.SHORT_DATE, 'M/D')
            .set(DATE_FORMAT.SHORT_DATE_TIME, 'M/D');

        this.dateWithTime = [
            DATE_FORMAT.LONG_FULL_DATE_TIME,
            DATE_FORMAT.SHORT_FULL_DATE_TIME,
            DATE_FORMAT.LONG_DATE_TIME,
            DATE_FORMAT.SHORT_DATE_TIME,
            DATE_FORMAT.TIME,
        ];
    }

    getFormattedDate(
        timestamp: number,
        format: DATE_FORMAT,
        isUTC = true,
        keepLocalTime = false,
        showSeconds = false
    ): string {
        let date = moment(timestamp);
        let datePart = '';
        let timePart = '';

        if (isUTC) {
            date = date.utc(keepLocalTime);
        }

        if (this.dateFormats.get(format)) {
            datePart = date.format(this.dateFormats.get(format));
        }

        if (this.dateWithTime.includes(format)) {
            timePart = this.getFormattedTime(date, showSeconds);
        }

        return [datePart, timePart].join(' ').trim();
    }

    getDuration(
        elapsedTime: number,
        abbreviate = false,
        showMilliseconds = false,
        datePartsSlice = 0
    ): string {
        const duration = moment.duration(elapsedTime);

        let parts = [
            {
                value: Math.floor(duration.asYears()),
                plural: 'years',
                singular: 'year',
                abbreviation: 'y',
            },
            {
                value: Math.floor(duration.months()),
                plural: 'months',
                singular: 'month',
                abbreviation: 'mo',
            },
            {
                value: Math.floor(duration.days()),
                plural: 'days',
                singular: 'day',
                abbreviation: 'd',
            },
            {
                value: Math.floor(duration.hours()),
                plural: 'hours',
                singular: 'hour',
                abbreviation: 'h',
            },
            { value: duration.minutes(), plural: 'minutes', singular: 'minute', abbreviation: 'm' },
        ];

        const secondsPart = {
            value: duration.seconds(),
            plural: 'seconds',
            singular: 'second',
            abbreviation: 's',
        };
        parts.push(secondsPart);

        if (showMilliseconds && secondsPart.value === 0) {
            parts.push({
                value: duration.milliseconds(),
                plural: 'milliseconds',
                singular: 'millisecond',
                abbreviation: 'ms',
            });
        }

        parts = parts.filter((item) => item.value > 0);

        if (datePartsSlice > 0) {
            parts = parts.slice(0, datePartsSlice);
        }

        return parts.reduce((previous, current, index) => {
            let trailing = '';
            if (!!previous && parts.length > 1) {
                trailing = index < parts.length - 1 ? ', ' : ' and ';
            }

            let currentStr = current.value.toString();
            if (abbreviate) {
                currentStr += current.abbreviation;
            } else if (current.value > 1) {
                currentStr += ' ' + current.plural;
            } else {
                currentStr += ' ' + current.singular;
            }
            return `${previous}${trailing}${currentStr}`;
        }, '');
    }

    getAdjacentTimeRange(
        dateRange: DateRange,
        timeRangeRequest: TIME_RANGE_REQUEST_TYPE
    ): DateRange {
        const from = moment(dateRange.from);
        let to = moment(dateRange.to);

        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        let diffUnit: any = dateRange.unit;
        const multiplyBy = timeRangeRequest === TIME_RANGE_REQUEST_TYPE.NEXT ? 1 : -1;

        if (dateRange.interval === TIME_INTERVAL.CUSTOM) {
            diffUnit = 'days';
            const diff = moment(to).add(1, 'milliseconds').diff(from, 'days');
            from.add(diff * multiplyBy, diffUnit);
            to.add(diff * multiplyBy, diffUnit);
        } else {
            from.add(1 * multiplyBy, diffUnit);
            to = moment(from).endOf(diffUnit);
        }

        return {
            ...dateRange,
            from: from.valueOf(),
            to: to.valueOf(),
        };
    }

    private getFormattedTime(date: moment.Moment, showSeconds: boolean): string {
        let time = date.format(
            showSeconds
                ? this.userPreferencesStore.userPreferenceTimeFormatWithSeconds
                : this.userPreferencesStore.userPreferenceTimeFormat
        );

        if (this.userPreferencesStore.userPreferences.use24HourClock) {
            const hours = date.hours();
            const minutes = date.minutes().toString().padStart(2, '0');
            const seconds = date.seconds().toString().padStart(2, '0');

            if (showSeconds) {
                time = hours === 0 ? `00:${minutes}:${seconds}` : time;
            } else {
                time = hours === 0 ? `00:${minutes}` : time;
            }
        }

        return time;
    }
}
